import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import { RoutingService } from 'src/app/services/routing.service';
import { HttpService } from 'src/app/services/http.service';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { Textarea } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { TabsModule } from 'primeng/tabs';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import {
  UpsertKeepaProductsGQL,
  UpsertKeepaProductInput,
  KeepaProductsWithStockGQL,
  DeleteAmazonProductsGQL,
} from 'src/gql/generated';
import { MessageService } from 'src/app/services/message.service';
import { firstValueFrom } from 'rxjs';
import { gqlErrors } from 'src/app/utils/gql-errors';

interface KeepaPreviewRow {
  asin: string;
  boughtPastMonth: number;
  categoryNames: string[];
}

type ViewState = 'input' | 'preview' | 'submitting' | 'done';

@Component({
  selector: 'app-keepa',
  templateUrl: './keepa.component.html',
  styleUrls: ['./keepa.component.sass'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ProgressSpinnerModule,
    MessageModule,
    TableModule,
    Textarea,
    CheckboxModule,
    TabsModule,
    ConfirmDialogModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ConfirmationService],
})
export class KeepaComponent {
  viewState: ViewState = 'input';
  tsvText = '';
  previewRows: KeepaPreviewRow[] = [];
  parseErrorCount = 0;
  resultMessage = '';
  resultSeverity: 'success' | 'error' = 'success';
  downloading = false;
  onlyMissingKeepa = false;

  // 削除タブ用
  deleteAsinsText = '';
  deleteResultMessage = '';
  deleteResultSeverity: 'success' | 'error' = 'success';
  deleting = false;
  deleteShowResult = false;

  constructor(
    private subtitleService: SubtitleService,
    private userService: UserService,
    private routingService: RoutingService,
    private http: HttpService,
    private upsertKeepaProductsGQL: UpsertKeepaProductsGQL,
    private keepaProductsWithStockGQL: KeepaProductsWithStockGQL,
    private deleteAmazonProductsGQL: DeleteAmazonProductsGQL,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {
    this.routingService.loginRequired();
    this.userService.keepaRequired();
    this.subtitleService.setSubTitle('Keepa商品連携');
  }

  onParse() {
    const lines = this.tsvText.trim().split('\n');
    if (lines.length < 2) {
      return;
    }

    const headers = lines[0].split('\t');
    const asinIndex = headers.indexOf('ASIN');
    const boughtIndex = headers.findIndex((h) => h.includes('先月の購入'));
    const categoryRootIndex = headers.indexOf('カテゴリ: ルート');
    const categorySubIndex = headers.indexOf('カテゴリ: サブ');

    if (asinIndex === -1 || boughtIndex === -1) {
      this.resultMessage = 'ヘッダーにASINまたは先月の購入が見つかりません';
      this.resultSeverity = 'error';
      this.viewState = 'done';
      return;
    }

    this.previewRows = [];
    this.parseErrorCount = 0;

    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split('\t');
      const asin = cols[asinIndex]?.trim();
      const boughtRaw = cols[boughtIndex]?.trim().replace(/,/g, '');
      const boughtPastMonth = boughtRaw === '' ? 9 : parseInt(boughtRaw, 10);

      if (!asin || !/^[A-Z0-9]{10}$/.test(asin) || isNaN(boughtPastMonth)) {
        this.parseErrorCount++;
        continue;
      }

      const categoryNames: string[] = [];
      if (categoryRootIndex !== -1 && cols[categoryRootIndex]?.trim()) {
        categoryNames.push(cols[categoryRootIndex].trim());
      }
      if (categorySubIndex !== -1 && cols[categorySubIndex]?.trim()) {
        categoryNames.push(cols[categorySubIndex].trim());
      }

      this.previewRows.push({ asin, boughtPastMonth, categoryNames });
    }

    if (this.previewRows.length === 0) {
      this.resultMessage = 'パース可能な行がありませんでした';
      this.resultSeverity = 'error';
      this.viewState = 'done';
      return;
    }

    this.viewState = 'preview';
  }

  onBack() {
    this.viewState = 'input';
  }

  async onSubmit() {
    this.viewState = 'submitting';

    const input: UpsertKeepaProductInput[] = this.previewRows.map((row) => ({
      asin: row.asin,
      boughtPastMonth: row.boughtPastMonth,
      categoryNames: row.categoryNames.length > 0 ? row.categoryNames : undefined,
    }));

    const resp = await firstValueFrom(this.upsertKeepaProductsGQL.mutate({ variables: { input } }));
    if (gqlErrors(resp)) {
      this.resultMessage = gqlErrors(resp)[0]?.message || 'エラーが発生しました';
      this.resultSeverity = 'error';
    } else {
      const count = resp.data?.upsertKeepaProducts?.keepaProducts?.length || 0;
      this.resultMessage = `${count}件の商品を登録しました`;
      this.resultSeverity = 'success';
    }
    this.viewState = 'done';
  }

  async onDownloadTsv() {
    this.downloading = true;
    try {
      const resp = await firstValueFrom(
        this.keepaProductsWithStockGQL.fetch({ variables: { onlyMissingKeepa: this.onlyMissingKeepa || undefined } }),
      );
      const products = resp.data.keepaProductsWithStock;
      const header = 'ASIN\t前月実績数\tらくじゃん取り込み日\tKeepa月間販売個数取り込み日';
      const rows = products.map((p) => {
        const bought = p.boughtPastMonth != null ? String(p.boughtPastMonth) : '';
        const storeImported = p.storeImportedAt ?? '';
        const keepaImported = p.keepaImportedAt ?? '';
        return `${p.asin}\t${bought}\t${storeImported}\t${keepaImported}`;
      });
      const tsv = [header, ...rows].join('\n');
      const blob = new Blob([tsv], { type: 'text/tab-separated-values;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'keepa_products.tsv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      this.messageService.setFlashMessage('error', 'エラー', 'ダウンロードに失敗しました');
    } finally {
      this.downloading = false;
    }
  }

  onReset() {
    this.tsvText = '';
    this.previewRows = [];
    this.parseErrorCount = 0;
    this.resultMessage = '';
    this.viewState = 'input';
  }

  onDeleteConfirm() {
    const lines = this.deleteAsinsText.trim().split('\n');
    const validAsins: string[] = [];
    let errorCount = 0;

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      if (/^[A-Z0-9]{10}$/.test(trimmed)) {
        validAsins.push(trimmed);
      } else {
        errorCount++;
      }
    }

    if (validAsins.length === 0) {
      this.deleteResultMessage = '有効なASINがありません';
      this.deleteResultSeverity = 'error';
      this.deleteShowResult = true;
      return;
    }

    const errorNote = errorCount > 0 ? `\n（${errorCount}件の不正な行をスキップ）` : '';
    this.confirmationService.confirm({
      message: `${validAsins.length}件のASINに対応するAmazon商品と全関連データを削除します。よろしいですか？${errorNote}`,
      header: '一括削除確認',
      acceptLabel: '削除',
      rejectLabel: 'キャンセル',
      accept: async () => {
        await this.executeDelete(validAsins);
      },
    });
  }

  private async executeDelete(asins: string[]) {
    this.deleting = true;
    this.deleteShowResult = false;
    try {
      const resp = await firstValueFrom(this.deleteAmazonProductsGQL.mutate({ variables: { asins } }));
      if (gqlErrors(resp)) {
        this.deleteResultMessage = gqlErrors(resp)[0]?.message || 'エラーが発生しました';
        this.deleteResultSeverity = 'error';
      } else {
        this.deleteResultMessage = `${asins.length}件のAmazon商品を削除しました`;
        this.deleteResultSeverity = 'success';
      }
    } catch {
      this.deleteResultMessage = '削除中にエラーが発生しました';
      this.deleteResultSeverity = 'error';
    } finally {
      this.deleting = false;
      this.deleteShowResult = true;
    }
  }

  onDeleteReset() {
    this.deleteAsinsText = '';
    this.deleteResultMessage = '';
    this.deleteShowResult = false;
  }
}
